import React, { useEffect, useState } from "react";
import { Play, MagnifyingGlass, Books } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

export default function ExerciseLibrary() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/library/exercises").then(({ data }) => setItems(data));
  }, []);

  const muscles = ["All", ...Array.from(new Set(items.map((e) => e.muscle)))];
  const filtered = items.filter((e) =>
    (filter === "All" || e.muscle === filter) &&
    e.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="exercises-page">
      <div className="mb-8">
        <div className="label-tiny text-brand-accent mb-2">// EXERCISE INTEL</div>
        <h1 className="font-heading text-4xl sm:text-5xl mb-3">EXERCISE LIBRARY</h1>
        <p className="text-text-secondary text-sm">Video demonstrations and form cues for every movement.</p>
      </div>

      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
          <Input
            placeholder="Search exercises..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-surface-elevated border-white/10"
            data-testid="exercise-search-input"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {muscles.map((m) => (
            <button
              key={m}
              onClick={() => setFilter(m)}
              className={`px-4 py-2 text-xs label-tiny tracking-wider border transition-colors whitespace-nowrap ${
                filter === m ? "border-brand-accent text-brand-accent bg-brand-accent/10" : "border-white/10 text-text-secondary hover:border-white/40"
              }`}
              data-testid={`muscle-filter-${m}`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-white/10" data-testid="exercises-grid">
        {filtered.map((e) => (
          <button
            key={e.id}
            onClick={() => setSelected(e)}
            className="bg-surface-DEFAULT hover:bg-surface-elevated transition-colors text-left group"
            data-testid={`exercise-card-${e.id}`}
          >
            <div className="aspect-video relative overflow-hidden bg-black">
              <img src={e.poster} alt={e.name} className="w-full h-full object-cover opacity-60 group-hover:opacity-90 transition-opacity" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-12 h-12 rounded-full bg-brand-primary/80 backdrop-blur flex items-center justify-center group-hover:bg-brand-accent transition-colors">
                  <Play size={20} weight="fill" className="text-white group-hover:text-surface-base ml-1" />
                </div>
              </div>
            </div>
            <div className="p-4">
              <div className="label-tiny text-brand-accent mb-1">{e.muscle}</div>
              <h3 className="font-heading text-base">{e.name}</h3>
              <div className="label-tiny text-text-secondary mt-1">{e.difficulty}</div>
            </div>
          </button>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="tactical-card p-12 text-center mt-4">
          <Books size={36} className="text-brand-primary mx-auto mb-4" />
          <p className="text-text-secondary">No exercises match your filter.</p>
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="bg-surface-DEFAULT border-white/10 text-white max-w-3xl" data-testid="exercise-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{selected?.name}</DialogTitle>
          </DialogHeader>
          {selected && (
            <div>
              <div className="flex gap-4 mb-4 text-xs">
                <span className="label-tiny text-brand-accent">{selected.muscle}</span>
                <span className="label-tiny text-text-secondary">{selected.equipment}</span>
                <span className="label-tiny text-text-secondary">{selected.difficulty}</span>
              </div>
              <div className="aspect-video bg-black mb-4 relative overflow-hidden">
                {selected.video && selected.video.includes("results?search_query") ? (
                  <a
                    href={selected.video}
                    target="_blank"
                    rel="noreferrer"
                    className="absolute inset-0 flex flex-col items-center justify-center group"
                    data-testid="exercise-watch-youtube"
                  >
                    <img src={selected.poster} alt={selected.name} className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 transition-opacity" />
                    <div className="relative z-10 flex flex-col items-center gap-2">
                      <div className="w-14 h-14 rounded-full bg-red-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Play size={26} weight="fill" className="text-white ml-1" />
                      </div>
                      <span className="label-tiny text-white tracking-widest">WATCH ON YOUTUBE</span>
                    </div>
                  </a>
                ) : (
                  <iframe
                    src={selected.video}
                    title={selected.name}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                )}
              </div>
              <div>
                <div className="label-tiny mb-2">FORM CUES</div>
                <p className="text-sm text-text-secondary">{selected.instructions}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
